import { useState } from 'react';
import { Loader2, ChevronUp, ChevronDown, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useImportJobs } from '@/hooks/use-import-jobs';
import { useNavigate } from 'react-router-dom';

export function ImportStatusIndicator(): React.ReactElement | null {
  const { activeJobs } = useImportJobs();
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();

  if (activeJobs.length === 0) {
    return null;
  }

  const totalProgress = activeJobs.reduce((sum, job) => {
    const progress = job.totalRows > 0 ? (job.processedRows / job.totalRows) * 100 : 0;
    return sum + progress;
  }, 0) / activeJobs.length;

  return (
    <div className="fixed bottom-20 right-4 z-50 w-80" data-testid="import-status-indicator">
      <Card className="shadow-lg border-2">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              <CardTitle className="text-sm">
                {activeJobs.length} Import{activeJobs.length > 1 ? 's' : ''} Running
              </CardTitle>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/administration/imports')}
                className="h-8 w-8 p-0"
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsExpanded(!isExpanded)}
                className="h-8 w-8 p-0"
                data-testid="import-status-expand-btn"
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronUp className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Overall Progress</span>
              <span>{Math.round(totalProgress)}%</span>
            </div>
            <Progress value={totalProgress} className="h-2" />
          </div>

          {isExpanded && (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {activeJobs.map((job) => {
                const progress = job.totalRows > 0 
                  ? (job.processedRows / job.totalRows) * 100 
                  : 0;

                return (
                  <div key={job.id} className="space-y-1 p-2 bg-muted/50 rounded-md" data-testid="import-job-detail">
                    <div className="flex justify-between items-start">
                      <p className="text-xs font-medium truncate flex-1">
                        {job.fileName}
                      </p>
                      <span className="text-xs text-muted-foreground ml-2">
                        {Math.round(progress)}%
                      </span>
                    </div>
                    <Progress value={progress} className="h-1" />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{job.processedRows.toLocaleString()} / {job.totalRows.toLocaleString()}</span>
                      <span className="text-green-600">{job.successCount} ✓</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
